import { useState, useEffect, useCallback, useRef } from "react";
import { useSession } from "@clerk/clerk-react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  FloppyDisk,
  Download,
  Copy,
} from "@phosphor-icons/react";
import {
  RealEstatePricingTable,
  ModulePricingEditor,
  TierPricingEditor,
  SavedSimulationsList,
  CommercialPreview,
  RevenueSimulator,
  FunctionalityEditor,
  OfferBuilder,
} from "./components";
import {
  PricingSimulation,
  ComparisonRow,
  RealEstatePricingModelMap,
  CategoryMultipliers,
  ModulePricing,
  TierPricing,
  YearSlice,
  DEFAULT_RE_PRICING_MODEL,
  DEFAULT_CATEGORY_MULTIPLIERS,
  VolumeDiscounts,
  DEFAULT_VOLUME_DISCOUNTS,
  DEFAULT_MODULE_PRICING,
  DEFAULT_TIER_PRICING,
  DEFAULT_COMPARISON_ROWS,
  createDefaultYear,
} from "./types";

const currentYear = new Date().getFullYear();
const BASE_SIM_NAME = "__base__";
const AUTOSAVE_DELAY = 2000;

export const PricingSimulatorPage = () => {
  const { session } = useSession();

  // Shared config (modules, RE model, multipliers)
  const [rePricingModel, setRePricingModel] =
    useState<RealEstatePricingModelMap>({ ...DEFAULT_RE_PRICING_MODEL });
  const [categoryMultipliers, setCategoryMultipliers] =
    useState<CategoryMultipliers>({ ...DEFAULT_CATEGORY_MULTIPLIERS });
  const [modulePricing, setModulePricing] =
    useState<ModulePricing>(DEFAULT_MODULE_PRICING);
  const [volumeDiscounts, setVolumeDiscounts] =
    useState<VolumeDiscounts>({ ...DEFAULT_VOLUME_DISCOUNTS });

  // Per-simulation state
  const [activeSimulationId, setActiveSimulationId] = useState<number | null>(null);
  const [activeVersionName, setActiveVersionName] = useState<string | null>(null);
  const [tierPricing, setTierPricing] = useState<TierPricing>(DEFAULT_TIER_PRICING);
  const [customPricingThresholdUnits, setCustomPricingThresholdUnits] = useState(200);
  const [customPricingThresholdM2, setCustomPricingThresholdM2] = useState(5000);
  const [comparisonRows, setComparisonRows] = useState<ComparisonRow[]>(DEFAULT_COMPARISON_ROWS);
  const [years, setYears] = useState<YearSlice[]>([createDefaultYear(currentYear)]);

  // Dirty tracking — show unsaved indicator
  const [configSavedHash, setConfigSavedHash] = useState("");
  const currentConfigHash = JSON.stringify({ modulePricing, tierPricing, rePricingModel, categoryMultipliers, volumeDiscounts });
  const hasUnsavedChanges = configSavedHash !== "" && configSavedHash !== "SYNC" && configSavedHash !== currentConfigHash;

  // When "SYNC" is set (after loading config), sync the hash to current state
  useEffect(() => {
    if (configSavedHash === "SYNC") {
      setConfigSavedHash(currentConfigHash);
    }
  }, [configSavedHash, currentConfigHash]);

  // Versions list (excludes base)
  const [versions, setVersions] = useState<PricingSimulation[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // ── Load shared config ──
  const configLoadedRef = useRef(false);
  const loadSharedConfig = useCallback(async () => {
    if (!session) return;
    // Only load once — don't overwrite unsaved local changes on session refresh
    if (configLoadedRef.current) return;
    configLoadedRef.current = true;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/pricing-simulations/config/shared`,
        { method: "GET" },
        session
      );
      if (response.ok) {
        const config = await response.json();
        if (config) {
          console.log("[PricingSimulator] Loaded shared config:", Object.keys(config));
          if (config.modulePricing) {
            // Reorder modules based on saved moduleOrder (PostgreSQL JSONB doesn't preserve key order)
            if (config.moduleOrder?.length) {
              const ordered: Record<string, any> = {};
              for (const k of config.moduleOrder) {
                if (config.modulePricing[k]) ordered[k] = config.modulePricing[k];
              }
              // Add any modules not in the order array (newly added)
              for (const k of Object.keys(config.modulePricing)) {
                if (!ordered[k]) ordered[k] = config.modulePricing[k];
              }
              setModulePricing(ordered);
            } else {
              setModulePricing(config.modulePricing);
            }
          }
          if (config.tierPricing && Object.keys(config.tierPricing).length > 0) {
            // Reorder tiers based on saved tierOrder
            if (config.tierOrder?.length) {
              const ordered: Record<string, any> = {};
              for (const k of config.tierOrder) {
                if (config.tierPricing[k]) ordered[k] = config.tierPricing[k];
              }
              for (const k of Object.keys(config.tierPricing)) {
                if (!ordered[k]) ordered[k] = config.tierPricing[k];
              }
              setTierPricing(ordered);
            } else {
              setTierPricing(config.tierPricing);
            }
            console.log("[PricingSimulator] Loading tiers:", Object.keys(config.tierPricing));
          }
          if (config.rePricingModel) setRePricingModel(config.rePricingModel);
          if (config.categoryMultipliers) setCategoryMultipliers(config.categoryMultipliers);
          if (config.volumeDiscounts) setVolumeDiscounts(config.volumeDiscounts);
          // Mark as needing hash sync — the useEffect will pick up "SYNC" after React re-renders with the new state
          setConfigSavedHash("SYNC");
        } else {
          console.log("[PricingSimulator] No shared config found, using defaults");
          setConfigSavedHash(currentConfigHash);
        }
      } else {
        console.error("[PricingSimulator] Failed to load config:", response.status);
      }
    } catch (error) {
      console.error("[PricingSimulator] Error loading shared config:", error);
    }
  }, [session]);

  // ── Save shared config ──
  const handleSaveSharedConfig = async () => {
    if (!session) return;
    setSavingConfig(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/pricing-simulations/config/shared`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            modulePricing, rePricingModel, categoryMultipliers, volumeDiscounts, tierPricing,
            moduleOrder: Object.keys(modulePricing),
            tierOrder: Object.keys(tierPricing),
          }),
        },
        session
      );
      if (response.ok) {
        console.log("[PricingSimulator] Config saved. Tiers:", Object.keys(tierPricing));
        toast.success("Gedeelde configuratie opgeslagen");
        setConfigSavedHash(currentConfigHash);
      } else {
        const errorText = await response.text();
        console.error("[PricingSimulator] Save config failed:", response.status, errorText);
        toast.error(`Fout bij opslaan: ${response.status} - ${errorText.substring(0, 100)}`);
      }
    } catch (error) {
      console.error("Error saving shared config:", error);
      toast.error("Fout bij opslaan gedeelde configuratie");
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Load base simulation + versions ──
  const fetchAll = useCallback(async () => {
    if (!session) return;
    setLoadingVersions(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/pricing-simulations`,
        { method: "GET" },
        session
      );
      if (response.ok) {
        const all: PricingSimulation[] = await response.json();
        const base = all.find((s) => s.name === BASE_SIM_NAME);
        const rest = all.filter((s) => s.name !== BASE_SIM_NAME);
        setVersions(rest);

        // Load base if we're not viewing a specific version
        if (base && !activeVersionName) {
          loadSimData(base);
          setActiveSimulationId(base.id || null);
        }
      }
    } catch (error) {
      console.error("Error fetching simulations:", error);
    } finally {
      setLoadingVersions(false);
    }
  }, [session]);

  useEffect(() => {
    // Load shared config first (modules, tiers, RE model, multipliers, discounts)
    // Then load simulations (only per-simulation data: years, comparison rows)
    loadSharedConfig().then(() => fetchAll());
  }, [loadSharedConfig, fetchAll]);

  // ── Load sim data into state (only per-simulation data, NOT shared config) ──
  const loadSimData = (sim: PricingSimulation) => {
    const cv = sim.clientVolumes as any;
    if (cv?.years) {
      setCustomPricingThresholdUnits(cv.customPricingThresholdUnits ?? 200);
      setCustomPricingThresholdM2(cv.customPricingThresholdM2 ?? 5000);
      setComparisonRows(cv.comparisonRows ?? DEFAULT_COMPARISON_ROWS);
      setYears(cv.years);
    }
  };

  // ── Build payload (only per-simulation data — shared config is saved separately) ──
  const buildPayload = (simName: string) => ({
    name: simName,
    realEstatePricing: {},  // not used for per-sim
    modulePricing: {},      // not used for per-sim
    tierPricing: {},        // not used for per-sim
    clientVolumes: { customPricingThresholdUnits, customPricingThresholdM2, comparisonRows, years },
  });

  // ── Auto-save base simulation (debounced) ──
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSave = useCallback(async () => {
    if (!session) return;
    try {
      const payload = buildPayload(BASE_SIM_NAME);
      const url = activeSimulationId && !activeVersionName
        ? `${API_URL}/pricing-simulations/${activeSimulationId}`
        : `${API_URL}/pricing-simulations`;
      const method = activeSimulationId && !activeVersionName ? "PUT" : "POST";
      const response = await fetchWithAuth(
        url,
        { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
        session
      );
      if (response.ok) {
        const saved = await response.json();
        if (!activeVersionName) setActiveSimulationId(saved.id);
      }
    } catch (error) {
      console.error("Auto-save failed:", error);
    }
  }, [session, activeSimulationId, activeVersionName, customPricingThresholdUnits, customPricingThresholdM2, comparisonRows, years]);

  // Trigger auto-save on per-simulation state changes only
  useEffect(() => {
    if (!session || activeVersionName) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(autoSave, AUTOSAVE_DELAY);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [customPricingThresholdUnits, customPricingThresholdM2, comparisonRows, years, autoSave, session, activeVersionName]);

  // ── Create a named version (copy from current state) ──
  const [newVersionName, setNewVersionName] = useState("");
  const handleCreateVersion = async () => {
    if (!session || !newVersionName.trim()) {
      toast.warning("Geef een naam op voor de versie");
      return;
    }
    try {
      const payload = buildPayload(newVersionName.trim());
      const response = await fetchWithAuth(
        `${API_URL}/pricing-simulations`,
        { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
        session
      );
      if (response.ok) {
        toast.success(`Versie "${newVersionName.trim()}" aangemaakt`);
        setNewVersionName("");
        fetchAll();
      }
    } catch (error) {
      console.error("Error creating version:", error);
      toast.error("Fout bij aanmaken versie");
    }
  };

  // ── Load a version ──
  const handleLoadVersion = (sim: PricingSimulation) => {
    loadSimData(sim);
    setActiveSimulationId(sim.id || null);
    setActiveVersionName(sim.name);
    toast.info(`Versie "${sim.name}" geladen`);
  };

  // ── Back to base ──
  const handleBackToBase = () => {
    setActiveVersionName(null);
    fetchAll(); // reload base
  };

  // ── Duplicate version ──
  const handleDuplicate = (sim: PricingSimulation) => {
    loadSimData(sim);
    setActiveVersionName(null);
    setNewVersionName(`${sim.name} (kopie)`);
    toast.info("Versie gekopieerd — geef een naam en sla op");
  };

  // ── Delete version ──
  const handleDelete = async (id: number) => {
    if (!session) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/pricing-simulations/${id}`,
        { method: "DELETE" },
        session
      );
      if (response.ok) {
        toast.success("Versie verwijderd");
        if (activeSimulationId === id) handleBackToBase();
        fetchAll();
      }
    } catch (error) {
      console.error("Error deleting version:", error);
      toast.error("Fout bij het verwijderen");
    }
  };

  // ── Save current version (when viewing a named version) ──
  const handleSaveVersion = async () => {
    if (!session || !activeSimulationId || !activeVersionName) return;
    try {
      const payload = buildPayload(activeVersionName);
      await fetchWithAuth(
        `${API_URL}/pricing-simulations/${activeSimulationId}`,
        { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
        session
      );
      toast.success("Versie bijgewerkt");
      fetchAll();
    } catch (error) {
      console.error("Error saving version:", error);
      toast.error("Fout bij opslaan versie");
    }
  };

  const handleExportHtml = () => {
    const previewEl = document.getElementById("commercial-preview");
    if (!previewEl) return;
    const html = `<!DOCTYPE html>
<html lang="nl"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Yally - Prijzen</title>
<script src="https://cdn.tailwindcss.com"><\/script>
<link href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet">
</head><body style="font-family: 'Sora', sans-serif;">${previewEl.innerHTML}</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `yally-pricing-${(activeVersionName || "base").replace(/\s+/g, "-").toLowerCase()}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("HTML ge\u00ebxporteerd");
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Prijssimulator | Yally</title>
      </Helmet>

      <div className="flex flex-col gap-6 p-4 max-w-[1400px] mx-auto pb-20">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold tracking-tight">Prijssimulator</h1>
            <p className="text-sm text-neutral-secondary-mid">
              {activeVersionName ? (
                <>
                  Versie: <span className="font-semibold text-primary">{activeVersionName}</span>
                  <button className="ml-2 text-xs underline text-primary" onClick={handleBackToBase}>
                    &larr; Terug naar basis
                  </button>
                </>
              ) : (
                <>Basissimulatie &mdash; sla wijzigingen op via de knop hieronder</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportHtml} className="gap-1.5">
              <Download size={14} /> Export HTML
            </Button>
          </div>
        </div>

        {/* Version management */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
          <div />
          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs">Versies</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="flex items-center gap-1.5 mb-2">
                <Input
                  value={newVersionName}
                  onChange={(e) => setNewVersionName(e.target.value)}
                  placeholder="Naam nieuwe versie..."
                  className="h-7 text-xs flex-1"
                  onKeyDown={(e) => e.key === "Enter" && handleCreateVersion()}
                />
                <Button size="sm" onClick={handleCreateVersion} disabled={!newVersionName.trim()} className="h-7 text-[10px] gap-1">
                  <Copy size={10} /> Kopie maken
                </Button>
              </div>
              <div className="max-h-[120px] overflow-y-auto">
                <SavedSimulationsList
                  simulations={versions}
                  loading={loadingVersions}
                  onLoad={handleLoadVersion}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="config" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="config">Interne configuratie</TabsTrigger>
            <TabsTrigger value="commercial">Commerci&euml;le weergave</TabsTrigger>
            <TabsTrigger value="revenue">Revenue simulator</TabsTrigger>
            <TabsTrigger value="offers">Klantoffertes</TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="space-y-6">

        {/* ── SHARED CONFIG ── */}
        <div className="border-b pb-2">
          <p className="text-xs font-bold text-neutral-secondary-mid uppercase tracking-wider">
            Gedeelde configuratie
          </p>
          <p className="text-[10px] text-neutral-secondary-mid">
            Modules en vastgoedclassificatie &mdash; gedeeld met alle gebruikers
          </p>
        </div>

        {/* 1. RE classification */}
        <section>
          <SectionHeading number={1} title="Vastgoedtype classificatie &amp; prijsfactor"
            subtitle="Bepaal het prijsmodel (m&#178; of unit) en het percentage van de basisprijs per categorie" />
          <RealEstatePricingTable
            pricingModel={rePricingModel} categoryMultipliers={categoryMultipliers} volumeDiscounts={volumeDiscounts}
            onModelChange={setRePricingModel} onMultipliersChange={setCategoryMultipliers} onVolumeDiscountsChange={setVolumeDiscounts}
          />
        </section>

        {/* 2. Module pricing */}
        <section>
          <SectionHeading number={2} title="Module prijzen"
            subtitle="Schakel modules in/uit en stel de prijs per eenheid per maand in" />
          <ModulePricingEditor pricing={modulePricing} onChange={setModulePricing}
            categoryMultipliers={categoryMultipliers} rePricingModel={rePricingModel} />
        </section>

        {/* 3. Functionalities per module */}
        <section>
          <SectionHeading number={3} title="Functionaliteiten per module"
            subtitle="Definieer welke functionaliteiten elke module biedt per tier (&#10004; = inbegrepen, tekst = aangepast)" />
          <FunctionalityEditor modulePricing={modulePricing} tierPricing={tierPricing} onModulePricingChange={setModulePricing} />
        </section>

        {/* ── PER-SIMULATION ── */}
        <div className="border-b pb-2">
          <p className="text-xs font-bold text-neutral-secondary-mid uppercase tracking-wider">
            {activeVersionName ? `Versie: ${activeVersionName}` : "Basissimulatie"}
          </p>
          <p className="text-[10px] text-neutral-secondary-mid">
            Tier-configuratie, prijzen en revenue
          </p>
        </div>

        {/* 3. Tiers */}
        <section>
          <SectionHeading number={4} title="Abonnementsniveaus &amp; module toewijzing"
            subtitle="Het verschil tussen tiers zit in welke modules inbegrepen zijn" />
          <TierPricingEditor pricing={tierPricing} modulePricing={modulePricing} onChange={setTierPricing} />
        </section>

          </TabsContent>

          <TabsContent value="commercial" className="space-y-6">

        {/* Commercial preview */}
        <section>
          <SectionHeading number={1} title="Commerci&#235;le preview"
            subtitle="Zo ziet de prijspagina eruit voor potenti&#235;le klanten" />
          <div id="commercial-preview">
            <CommercialPreview
              tierPricing={tierPricing} modulePricing={modulePricing} rePricingModel={rePricingModel}
              categoryMultipliers={categoryMultipliers} customPricingThresholdUnits={customPricingThresholdUnits}
              customPricingThresholdM2={customPricingThresholdM2} comparisonRows={comparisonRows}
              onModulePricingChange={setModulePricing}
            />
          </div>
        </section>

          </TabsContent>

          <TabsContent value="revenue" className="space-y-6">
            <RevenueSimulator
              tierPricing={tierPricing} modulePricing={modulePricing} rePricingModel={rePricingModel}
              categoryMultipliers={categoryMultipliers} years={years} onYearsChange={setYears}
            />
          </TabsContent>

          <TabsContent value="offers" className="space-y-6">
            <OfferBuilder
              tierPricing={tierPricing}
              modulePricing={modulePricing}
              rePricingModel={rePricingModel}
              categoryMultipliers={categoryMultipliers}
              volumeDiscounts={volumeDiscounts}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Fixed bottom save bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t px-4 py-2 ${hasUnsavedChanges ? "bg-amber-50/95 border-amber-200" : "bg-background/95"}`}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <p className="text-xs text-neutral-secondary-mid">
            {hasUnsavedChanges ? (
              <span className="text-amber-700 font-medium">Je hebt onopgeslagen wijzigingen, vergeet niet op te slaan</span>
            ) : (
              activeVersionName ? `Versie: ${activeVersionName}` : "Alle wijzigingen opgeslagen"
            )}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant={hasUnsavedChanges ? "default" : "outline"}
              size="sm"
              onClick={handleSaveSharedConfig}
              disabled={savingConfig || !hasUnsavedChanges}
              className="gap-1.5"
            >
              <FloppyDisk size={14} />
              {savingConfig ? "Opslaan..." : hasUnsavedChanges ? "Wijzigingen opslaan" : "Opgeslagen"}
            </Button>
            {activeVersionName && (
              <Button size="sm" onClick={handleSaveVersion} className="gap-1.5">
                <FloppyDisk size={14} /> Versie opslaan
              </Button>
            )}
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
};

function SectionHeading({ number, title, subtitle }: { number: number; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
        {number}
      </span>
      <div>
        <h2 className="text-sm font-bold tracking-tight" dangerouslySetInnerHTML={{ __html: title }} />
        <p className="text-xs text-neutral-secondary-mid" dangerouslySetInnerHTML={{ __html: subtitle }} />
      </div>
    </div>
  );
}

export default PricingSimulatorPage;
